import { useFn, useTimeout } from '@eturi/react'
import type { OnboardingUser } from '@op/services'
import { onboardingUsers$ } from '@op/services'
import random from 'lodash/random'
import { memo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useFontScale } from '../../../hooks'
import { setOnboardingPage } from '../../../reducers/app-misc.slice'
import { useAppDispatch } from '../../../types'
import { Glyph, LoadingSpinner } from '../../../widgets'
import { SnapRow } from '../../../widgets/SnapRow/SnapRow'
import {
	PROFILE_SETUP_HIGH_DURATION as HIGH_DURATION,
	PROFILE_SETUP_LOADING_MESSAGES as LOADING_MESSAGES,
	PROFILE_SETUP_LOW_DURATION as LOW_DURATION,
	ONBOARDING_PAGE_SUMMARY,
	PROFILE_SETUP_STEPS,
} from '../constants'
import { ChildProfilePreview } from '../Shared/ChildPreview/ChildProfilePreview'
import { OnboardingScreen } from './OnboardingScreen'

const OnboardingLoadingScreen = () => {
	const onboardingUsers = useSelector(onboardingUsers$)
	const [currentMsgIdx, setCurrentMsgIdx] = useState(0)
	const [nextStepTimeout] = useTimeout()
	const d = useAppDispatch()
	const fontScaled = useFontScale()

	useEffect(() => {
		const low = LOW_DURATION / LOADING_MESSAGES.length
		const high = HIGH_DURATION / LOADING_MESSAGES.length

		nextStepTimeout(
			() => {
				if (currentMsgIdx < LOADING_MESSAGES.length - 1) {
					setCurrentMsgIdx(currentMsgIdx + 1)
				} else {
					d(setOnboardingPage(ONBOARDING_PAGE_SUMMARY))
				}
			},
			random(low, high),
		)
	}, [currentMsgIdx])

	const renderItem = useFn(({ item, index }: { item: OnboardingUser; index: number }) => (
		<ChildProfilePreview
			idx={index}
			age={`Age ${item.age}`}
			name={item.name}
			direction="vertical"
			className="mx-auto"
		/>
	))

	return (
		<OnboardingScreen>
			<OnboardingScreen.Header>Creating Your Family's Digital Protection</OnboardingScreen.Header>

			<OnboardingScreen.Content className="gap-5 mb-4">
				<p className="font-semibold text-center text-balance">Setting up profiles for:</p>

				<div className="m-auto max-w-full">
					<SnapRow
						className="pb-3"
						items={onboardingUsers}
						gap={fontScaled(16)}
						itemWidth={fontScaled(92)}
						renderItem={renderItem}
					/>
				</div>
			</OnboardingScreen.Content>

			<OnboardingScreen.Content className="bg-teal-hl rounded-3xl p-4 pb-11">
				<LoadingSpinner className="is-md !mt-6" timeout={0} />

				<p className="text-sm font-bold">{LOADING_MESSAGES[currentMsgIdx]}</p>

				<div className="flex flex-col items-start gap-2 mt-6">
					{PROFILE_SETUP_STEPS.map((step, idx) => (
						<LoadingStep key={idx} isComplete={idx <= currentMsgIdx} step={step} />
					))}
				</div>
			</OnboardingScreen.Content>
		</OnboardingScreen>
	)
}

export default memo(OnboardingLoadingScreen)

type LoadingStepProps = {
	readonly isComplete: boolean
	readonly step: string
}

const LoadingStep = ({ isComplete, step }: LoadingStepProps) => (
	<div className="flex items-center gap-2">
		<Glyph
			className={isComplete ? 'text-teal' : 'refresh-spin text-charcoal'}
			name={isComplete ? 'check' : 'recurring'}
			size="sm"
		/>

		<p className="text-sm whitespace-pre-wrap">{step}</p>
	</div>
)
